// Per-status CRM user counts for the `crm-users` sidebar panel.
//
// The shared `@clarix/api-client` singleton points at the gateway root, but the
// CRM user directory lives under the `/crm` gateway prefix — so, exactly like
// the CRM master-config client in main.tsx, back these counts with a
// `/crm`-scoped client (same auth + payload handling via createScopedApi). The
// gateway can then read per-status totals without importing any CRM-remote code.

import { useQuery } from '@tanstack/react-query';
import { createScopedApi } from '@clarix/api-client';
import { env } from '../../config/env';

const crmUsersScopedApi = createScopedApi({
  baseURL: `${env.VITE_API_BASE_URL.replace(/\/+$/, '')}/crm`,
});

type CountResponse = { data: unknown[]; pagination: { total: number } };

const EMPTY_COUNTS = { all: 0, active: 0, deactive: 0 };

/**
 * One `page_size: 1` request per status, read off `pagination.total`, so the
 * panel always agrees with the CRM Users page tabs. The CRM list endpoint
 * excludes removed/deleted users and has NO separate "invited" bucket, so "All"
 * is simply active + deactivated.
 */
export function useCrmUserCounts() {
  // Cached so re-hovering the panel within a minute reuses the counts instead of refetching.
  const query = useQuery({
    queryKey: ['crm', 'user-counts'],
    queryFn: async () => {
      const [active, deactive] = await Promise.all([
        crmUsersScopedApi.baseService.get<CountResponse>('/users', {
          params: { is_active: true, page: 1, page_size: 1 },
        }),
        crmUsersScopedApi.baseService.get<CountResponse>('/users', {
          params: { is_active: false, page: 1, page_size: 1 },
        }),
      ]);
      const a = active.data.pagination.total;
      const d = deactive.data.pagination.total;
      return { all: a + d, active: a, deactive: d };
    },
    staleTime: 60_000,
  });

  return {
    counts: query.data ?? EMPTY_COUNTS,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
